import { useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { Bot, Clock, GitBranch, Plus, Radio, Rocket, Search, Sparkles, Workflow } from "lucide-react";
import { useApi, usePermission } from "@ncp/api-client";
import { formatDateTime, useLocale, useTranslation } from "@ncp/i18n";
import { Badge, Button, Card, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, EmptyState, Field, Input, NativeSelect, Skeleton, Textarea, cn, useSave } from "@ncp/ui";
import { repoKeys, useGallery } from "./api";
import type { BranchDetail, GalleryItem } from "./types";

/** Every workflow in the platform as a card: what is live, open branches, pending releases and running cases. */
export function WorkflowGallery() {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const navigate = useNavigate();
  const { can } = usePermission();
  const gallery = useGallery();
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const items = useMemo(() => (gallery.data ?? []).filter((w) => !q || `${w.name} ${w.nameAr} ${w.key} ${w.department ?? ""}`.toLowerCase().includes(q.toLowerCase())), [gallery.data, q]);
  const groups = useMemo(() => {
    const map = new Map<string, GalleryItem[]>();
    for (const w of items) { const g = w.department ?? (w.caseType === "Generic" ? "other" : "competition"); map.set(g, [...(map.get(g) ?? []), w]); }
    return [...map.entries()];
  }, [items]);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-72"><Search className="pointer-events-none absolute start-2.5 top-2.5 size-4 text-sand-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} className="ps-8" placeholder={t("admin:builder.gallery.search")} /></div>
        <span className="text-xs text-muted-foreground">{t("admin:builder.gallery.count", { count: items.length })}</span>
        <span className="flex-1" />
        {can("platform.workflows.write") ? <Button variant="gold" onClick={() => setCreating(true)}><Plus /> {t("admin:builder.gallery.new")}</Button> : null}
      </div>
      {gallery.isLoading ? <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{Array.from({ length: 6 }, (_, i) => <Skeleton key={i} className="h-60 rounded-2xl" />)}</div> : null}
      {!gallery.isLoading && items.length === 0 ? <EmptyState icon={Workflow} title={t("admin:builder.gallery.empty")} /> : null}
      {groups.map(([group, list]) => (
        <section key={group} className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-wider text-sand-600">{t(`admin:builder.gallery.groups.${group}`, { defaultValue: group })}</p>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {list.map((w) => (
              <Card key={w.key} className="group cursor-pointer overflow-hidden transition hover:-translate-y-0.5 hover:shadow-sand-lg" onClick={() => navigate(`/workflows/${w.key}`)}>
                <Thumbnail item={w} />
                <div className="space-y-2 p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0"><p className="truncate font-semibold">{locale === "ar" ? w.nameAr : w.name}</p><p className="font-mono text-[11px] text-muted-foreground">{w.key}</p></div>
                    {w.liveVersion ? <Badge variant="success" className="shrink-0"><Radio /> v{w.liveVersion}</Badge> : <Badge variant="outline" className="shrink-0">{t("admin:builder.gallery.notLive")}</Badge>}
                  </div>
                  <div className="flex flex-wrap gap-1.5 text-[11px] text-muted-foreground">
                    <span>{w.states} {t("admin:builder.gallery.states")}</span>·<span className="inline-flex items-center gap-0.5"><Clock className="size-3" />{w.clocks}</span>·<span className="inline-flex items-center gap-0.5"><Bot className="size-3" />{w.agents}</span>
                    {w.runningCases ? <>·<span className="font-medium text-aegold-800">{t("admin:builder.gallery.running", { count: w.runningCases })}</span></> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {w.openBranches ? <Badge size="sm" variant="gold"><GitBranch /> {t("admin:builder.gallery.branches", { count: w.openBranches })}</Badge> : null}
                    {w.pending ? <Badge size="sm" variant="warning"><Rocket /> {t("admin:builder.gallery.pending", { version: w.pending.version ?? "?" })}</Badge> : null}
                  </div>
                  {w.lastMessage ? <p className="truncate text-[11px] text-muted-foreground">{w.lastMessage} · {w.lastAuthor} · {formatDateTime(w.updatedAt, locale)}</p> : null}
                </div>
              </Card>
            ))}
          </div>
        </section>
      ))}
      <NewWorkflowDialog open={creating} onOpenChange={setCreating} templates={gallery.data ?? []} />
    </div>
  );
}

/** A small drawing of the workflow: saved positions when there are any, else columns by distance from the start. */
function Thumbnail({ item }: { item: GalleryItem }) {
  const layout = useMemo(() => {
    const hasPositions = item.nodes.some((n) => n.x != null);
    const pos = new Map<string, { x: number; y: number }>();
    if (hasPositions) item.nodes.forEach((n) => pos.set(n.key, { x: n.x ?? 0, y: n.y ?? 0 }));
    else {
      const start = item.nodes.find((n) => n.kind === "Start")?.key ?? item.nodes[0]?.key;
      const depth = new Map<string, number>();
      const queue = start ? [start] : [];
      if (start) depth.set(start, 0);
      while (queue.length) { const k = queue.shift()!; for (const e of item.edges.filter((x) => x.from === k)) if (!depth.has(e.to)) { depth.set(e.to, depth.get(k)! + 1); queue.push(e.to); } }
      const used = new Map<string, number>();
      item.nodes.forEach((n) => { const d = depth.get(n.key) ?? 0; const slot = `${d}:${n.lane}`; const b = used.get(slot) ?? 0; used.set(slot, b + 1); pos.set(n.key, { x: d * 260, y: n.lane * 140 + b * 60 }); });
    }
    const xs = [...pos.values()].map((p) => p.x); const ys = [...pos.values()].map((p) => p.y);
    const minX = Math.min(0, ...xs), minY = Math.min(0, ...ys), maxX = Math.max(1, ...xs) + 240, maxY = Math.max(1, ...ys) + 90;
    return { pos, box: `${minX - 20} ${minY - 20} ${maxX - minX + 40} ${maxY - minY + 40}` };
  }, [item]);
  const color = (k: string) => (k === "Start" ? "#16a34a" : k === "End" ? "#1f2937" : k === "Agent" ? "#2f6f9f" : k === "Automation" ? "#4f94c4" : k === "Decision" ? "#b68a35" : "#cfc5b1");
  return (
    <div className="h-32 border-b border-sand-100 bg-[radial-gradient(#e3dccd_1px,transparent_1px)] [background-size:14px_14px]">
      <svg viewBox={layout.box} preserveAspectRatio="xMidYMid meet" className="h-full w-full">
        {item.edges.map((e, i) => { const a = layout.pos.get(e.from); const b = layout.pos.get(e.to); return a && b ? <path key={i} d={`M${a.x + 240},${a.y + 45} C${a.x + 300},${a.y + 45} ${b.x - 60},${b.y + 45} ${b.x},${b.y + 45}`} stroke="#cfc5b1" strokeWidth={6} fill="none" /> : null; })}
        {item.nodes.map((n) => { const p = layout.pos.get(n.key)!; return <rect key={n.key} x={p.x} y={p.y} width={240} height={90} rx={n.kind === "End" ? 45 : 18} fill="white" stroke={color(n.kind)} strokeWidth={8} />; })}
      </svg>
    </div>
  );
}

function NewWorkflowDialog({ open, onOpenChange, templates }: { open: boolean; onOpenChange: (o: boolean) => void; templates: GalleryItem[] }) {
  const { t } = useTranslation();
  const api = useApi();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameAr, setNameAr] = useState("");
  const [key, setKey] = useState("");
  const [from, setFrom] = useState("");
  const [describe, setDescribe] = useState("");
  const slug = (key || name).trim().toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").replace(/^(\d)/, "w-$1").slice(0, 40);
  const create = useSave({
    mutationFn: () => api.post<BranchDetail>("/api/workflows/repos", { key: slug, name: name.trim(), nameAr: nameAr.trim() || null, fromKey: from || null }),
    saved: t("admin:builder.gallery.created", { name }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: repoKeys.gallery });
      onOpenChange(false);
      navigate(`/workflows/${slug}${describe.trim() ? `?panel=copilot&generate=${encodeURIComponent(describe.trim())}` : ""}`);
    },
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Workflow className="size-5 text-aegold-700" /> {t("admin:builder.gallery.new")}</DialogTitle><DialogDescription>{t("admin:builder.gallery.newHint")}</DialogDescription></DialogHeader>
        <Field label="Name (EN)" required>{(id) => <Input id={id} value={name} onChange={(e) => setName(e.target.value)} dir="ltr" />}</Field>
        <Field label="الاسم (AR)">{(id) => <Input id={id} value={nameAr} onChange={(e) => setNameAr(e.target.value)} dir="rtl" />}</Field>
        <Field label={t("admin:builder.gallery.key")} hint={t("admin:builder.gallery.keyHint")}>{(id) => <Input id={id} value={key || slug} onChange={(e) => setKey(e.target.value)} className="font-mono" dir="ltr" />}</Field>
        <Field label={t("admin:builder.gallery.startFrom")}>{(id) => <NativeSelect id={id} value={from} onChange={(e) => setFrom(e.target.value)}><option value="">{t("admin:builder.gallery.blank")}</option>{templates.map((w) => <option key={w.key} value={w.key}>{w.name}</option>)}</NativeSelect>}</Field>
        <Field label={<span className="inline-flex items-center gap-1"><Sparkles className="size-3.5 text-seablue-600" /> {t("admin:builder.gallery.describe")}</span>} hint={t("admin:builder.gallery.describeHint")}>{(id) => <Textarea id={id} rows={3} value={describe} onChange={(e) => setDescribe(e.target.value)} disabled={!!from} dir="auto" placeholder={t("admin:builder.gallery.describePlaceholder")} />}</Field>
        <DialogFooter><Button variant="ghost" onClick={() => onOpenChange(false)}>{t("actions.cancel")}</Button><Button variant="gold" loading={create.isPending} disabled={!name.trim() || slug.length < 2} className={cn()} onClick={() => create.mutate()}><Plus /> {t("admin:builder.gallery.create")}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
